import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { ModalService } from 'ui-components/services/modal-service';

import { ErrorModal } from '../../components/modals/error-modal';
import { UNV } from '../../enums/address-verification-status';
import { useOnComponentMount } from '../../hooks/use-on-component-mount';
import { getStore } from '../../pages/settings/api/store-api';
import Global from '../../store/global-selectors';
import { getParsedAddress } from '../address.api';
import { PASTE_ADDRESS } from '../constants';
import { useCountryAddressFormat } from '../countries/hooks';
import { InputLabel } from '../input-label';
import { messages } from '../messages';
import { PasteAddress } from '../paste-address';
import style from './style.module.less';

export const EditAddressComponent = ({
  address,
  addressWrapperClassName = '',
  email = '',
  onSave,
  onCancel,
  showAddressLine3,
  wrapperClassName = '',
}) => {
  const [editAddress, setEditAddress] = useState({ ...address, email });
  const [autoCorrect, setAutocorrect] = useState(false);
  const addressFormat = useCountryAddressFormat(editAddress.countryCode);
  const intl = useIntl();
  const salesOrderId = useSelector(Global.selectOrderDetailsSalesOrderId);
  const salesOrderEntity = useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId }));
  const storeGuid = salesOrderEntity?.store?.storeGuid;

  const getStoreSettings = useCallback(() => {
    if (storeGuid) {
      try {
        getStore(storeGuid).then((resp) => {
          const { bypassAddressSanitation, bypassInternationalAddressSanitation } = resp.settings;
          if (address.countryCode === 'US') {
            setAutocorrect(!bypassAddressSanitation);
          } else {
            setAutocorrect(!bypassInternationalAddressSanitation);
          }
        });
      } catch (error) {
        throw new Error(`Error getting store: ${error?.message}`);
      }
    }
  }, [storeGuid, address.countryCode]);

  useOnComponentMount(getStoreSettings);

  const updateAddress = (field, value) => {
    setEditAddress({
      ...editAddress,
      verificationStatus: UNV,
      [field]: value,
    });
  };

  const toggleAutoCorrect = () => setAutocorrect(!autoCorrect);

  const launchPasteAddressErrorModal = () => {
    ModalService.launchModal({
      component: ErrorModal,
      props: {
        title: intl.formatMessage(messages.parseFailTitle),
        message: intl.formatMessage(messages.parseFailMessage),
      },
    });
  };

  const mapPasteAddressResponse = (pastedAddress) => {
    setEditAddress({
      ...editAddress,
      verificationStatus: UNV,
      name: pastedAddress.name || '',
      company: pastedAddress.company || '',
      line1: pastedAddress.line1 || '',
      line2: pastedAddress.line2 || '',
      line3: pastedAddress.line3 || '',
      city: pastedAddress.city || '',
      state: pastedAddress.state || '',
      postalCode: pastedAddress.postalCode || '',
      countryCode: pastedAddress.countryCode || '',
      phone: pastedAddress.phone ?? editAddress.phone,
    });
  };

  const handlePasteAddressRequest = (value) => {
    getParsedAddress(value).subscribe(
      (response) => mapPasteAddressResponse(response),
      () => launchPasteAddressErrorModal(),
    );
  };

  const renderPasteAddress = (element) => {
    const placeholder = messages[element.placeholderMessageKey] ? intl.formatMessage(messages[element.placeholderMessageKey]) : '';
    return <PasteAddress key={element.id} pasteAddressRequest={handlePasteAddressRequest} placeholder={placeholder} />;
  };

  const handleSave = () => {
    onSave(editAddress, editAddress.email, autoCorrect);
  };

  const renderElement = (element) => {
    if (Array.isArray(element)) {
      return <div className={style['input-row']}>{element.map(renderElement)}</div>;
    }

    if (element.type === PASTE_ADDRESS) {
      return renderPasteAddress(element);
    }

    if (element.name === 'line3' && !showAddressLine3) {
      return null;
    }

    const { id, labelClassName, required, name, renderComponent, placeholderMessageKey, componentClassName } = element;

    const showLabel = messages[id] ? <FormattedMessage {...messages[id]} /> : null;

    const placeholder = messages[placeholderMessageKey] ? intl.formatMessage(messages[placeholderMessageKey]) : '';

    const props = {
      name,
      placeholder,
      componentClassName: style[componentClassName] || style['full-width-input'],
      countryCode: editAddress.countryCode,
      onChange: (value) => updateAddress(name, value),
      value: editAddress[name] || '',
    };

    return (
      <div key={id}>
        {showLabel ? (
          <InputLabel className={style[labelClassName]} required={required}>
            {showLabel}
          </InputLabel>
        ) : null}
        {renderComponent(props)}
      </div>
    );
  };

  const { elements } = addressFormat;
  const inputRows = elements.reduce((acc, el) => {
    if (el.isInline) {
      const prevEl = acc[acc.length - 1];
      if (Array.isArray(prevEl)) {
        prevEl.push(el);
      } else {
        acc.push([el]);
      }
    } else {
      acc.push(el);
    }
    return acc;
  }, []);
  return (
    <div className={classnames(style['edit-address-wrapper'], wrapperClassName)} data-testid='edit-address-form'>
      <div className={classnames(style['manual-address-wrapper'], addressWrapperClassName)}>{inputRows.map((el) => renderElement(el))}</div>
      {!!inputRows.length && (
        <div className={style['button-row']}>
          <Checkbox
            checked={autoCorrect}
            className={style['auto-correct-checkbox']}
            label={intl.formatMessage(messages.autoCorrect)}
            onChange={toggleAutoCorrect}
            value={autoCorrect}
          />
          <Button className={style['add-margin']} onClick={onCancel} theme='tertiary'>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button onClick={handleSave} theme='primary'>
            <FormattedMessage {...messages.save} />
          </Button>
        </div>
      )}
    </div>
  );
};

EditAddressComponent.propTypes = {
  address: PropTypes.shape({
    name: PropTypes.string,
    company: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    line3: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    countryCode: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  addressWrapperClassName: PropTypes.string,
  email: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  showAddressLine3: PropTypes.bool.isRequired,
  wrapperClassName: PropTypes.string,
};
